// A ride or route drawn as its own line: no map tiles, just the shape of where it went, in the
// Signal style. Equirectangular with the latitude's cosine, which is true enough at this scale.
// The start is a ring; home, when given and inside the frame, a small dot.
// With tiles, the line sits on a quiet map (CARTO's OpenStreetMap basemap) in Web Mercator.
import { decodePolyline } from "../lib/polyline";
import { ATTRIBUTION, frame, tileUrl, proxied } from "../lib/tiles";

const isDark = () => { const t = document.documentElement.dataset.theme; return t ? t === "dark" : matchMedia("(prefers-color-scheme: dark)").matches; };

type P = [number, number];
export function project(pts: P[], w: number, h: number, pad = 10) {
  const lat0 = pts.reduce((a, p) => a + p[0], 0) / pts.length;
  const k = Math.cos((lat0 * Math.PI) / 180);
  const xs = pts.map((p) => p[1] * k), ys = pts.map((p) => -p[0]);
  const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
  const sc = Math.min((w - pad * 2) / Math.max(1e-6, x1 - x0), (h - pad * 2) / Math.max(1e-6, y1 - y0));
  const ox = (w - (x1 - x0) * sc) / 2, oy = (h - (y1 - y0) * sc) / 2;
  return (p: P) => [ox + (p[1] * k - x0) * sc, oy + (-p[0] - y0) * sc] as [number, number];
}

export function RouteMap({ poly, points, h = 180, label, thumb, tiles, dark }: { poly?: string; points?: P[]; h?: number; label: string; thumb?: boolean; tiles?: boolean; dark?: boolean }) {
  const pts = points ?? (poly ? decodePolyline(poly) : []);
  if (pts.length < 2) return null;
  if (tiles && !thumb) return <TiledMap pts={pts} h={h} label={label} dark={dark} />;
  const w = thumb ? 96 : 360;
  const H = thumb ? 96 : h;
  const f = project(pts, w, H, thumb ? 8 : 16);
  const d = pts.map((p, i) => { const [x, y] = f(p); return `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`; }).join("");
  const [sx, sy] = f(pts[0]);
  return (
    <svg class={`route-map${thumb ? " thumb" : ""}`} viewBox={`0 0 ${w} ${H}`} role="img" aria-label={label} preserveAspectRatio="xMidYMid meet">
      <path d={d} class="rm-under" />
      <path d={d} class="rm-line" />
      <circle cx={sx} cy={sy} r={thumb ? 3 : 5} class="rm-start" />
    </svg>
  );
}

function TiledMap({ pts, h, label, dark: forceDark }: { pts: P[]; h: number; label: string; dark?: boolean }) {
  const w = 360, pad = 22;
  const fr = frame(pts, w, h, { x: pad, y: pad, w: w - pad * 2, h: h - pad * 2 });
  const dark = forceDark ?? isDark();
  const d = pts.map((p, i) => { const [x, y] = fr.at(p); return `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`; }).join("");
  const [sx, sy] = fr.at(pts[0]);
  return (
    <svg class="route-map tiled" viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label} preserveAspectRatio="xMidYMid slice">
      {fr.tiles.map((t) => <image href={proxied(tileUrl(t, dark))} x={t.px} y={t.py} width={t.size + 0.5} height={t.size + 0.5} preserveAspectRatio="none" />)}
      <rect width={w} height={h} class="rm-wash" />
      <path d={d} class="rm-under" />
      <path d={d} class="rm-line" />
      <circle cx={sx} cy={sy} r={5} class="rm-start" />
      <text x={w - 8} y={h - 7} text-anchor="end" class="rm-attr">{ATTRIBUTION}</text>
    </svg>
  );
}
